import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";
import { IDataPagination, User } from "../../types/User";

interface UserFormProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: User) => void;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  isOpen,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<Partial<User>>({
    firstName: "",
    lastName: "",
    email: "",
    isAdmin: false,
    permissions: [],
    warehouseId: undefined,
  });
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchData } = useFetchContext();
  const { showToast } = useToast();

  // Fetch warehouses for dropdown
  useEffect(() => {
    const getWarehouses = async () => {
      try {
        const response = await fetchData<IDataPagination<Warehouse>>(
          "/warehouses"
        );
        if (response && response.data && response.data.items) {
          setWarehouses(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        showToast("Errore nel caricamento dei magazzini", "error");
      }
    };

    getWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setForm(user);
    } else {
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        isAdmin: false,
        permissions: [],
        warehouseId: undefined,
      });
    }
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (name === "warehouseId") {
      // Handle warehouse selection
      setForm({
        ...form,
        warehouseId: value ? parseInt(value) : undefined,
      });
    } else {
      setForm({
        ...form,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ensure all required fields are filled
    if (!form.firstName || !form.lastName || !form.email) {
      showToast("Compila tutti i campi obbligatori", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const userData = {
        id: user?.id,
        uuid: user?.uuid,
        firstName: form.firstName || "",
        lastName: form.lastName || "",
        email: form.email || "",
        isAdmin: form.isAdmin || false,
        permissions: form.permissions || [],
        warehouseId: form.warehouseId,
        createdAt: user?.createdAt,
        updatedAt: new Date(),
      };

      onSave(userData);
      showToast(
        `Utente ${user ? "aggiornato" : "creato"} con successo`,
        "success"
      );
    } catch (error) {
      console.error("Error saving user:", error);
      showToast(
        `Errore durante il ${
          user ? "aggiornamento" : "salvataggio"
        } dell'utente`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? "Modifica Utente" : "Aggiungi Utente"}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome"
          name="firstName"
          value={form.firstName || ""}
          onChange={handleChange}
          required
          fullWidth
        />
        <Input
          label="Cognome"
          name="lastName"
          value={form.lastName || ""}
          onChange={handleChange}
          required
          fullWidth
        />
        <Input
          label="Email"
          name="email"
          type="email"
          value={form.email || ""}
          onChange={handleChange}
          required
          fullWidth
        />
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Magazzino
          </label>
          <select
            name="warehouseId"
            value={form.warehouseId || ""}
            onChange={handleChange}
            className="shadow-sm rounded-md border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 w-full"
          >
            <option value="">Nessun magazzino</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center">
          <input
            id="isAdmin"
            name="isAdmin"
            type="checkbox"
            checked={form.isAdmin || false}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-900">
            Amministratore
          </label>
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Annulla
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Salva
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserForm;
